import { useMemo } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useData } from "../context/DataContext";
import EmptyState from "../components/EmptyState";
import { Button } from "../components/ui/button";
import { formatDatePretty } from "../lib/progress";

export default function Makeups() {
    const { attendance, resolveMakeup } = useData();

    const pending = useMemo(
        () =>
            attendance
                .filter(
                    (r) =>
                        r.status === "Excused" &&
                        !(r.remark || "").startsWith("Done"),
                )
                .sort((a, b) => a.date.localeCompare(b.date)),
        [attendance],
    );

    const onResolve = (rec) => {
        resolveMakeup(rec.id);
        toast.success(`Marked make-up as done for ${rec.studentName}`);
    };

    const now = new Date();
    const isOverdue = (dateStr) => {
        const [y, m, d] = dateStr.split("-").map(Number);
        const diffDays = Math.floor(
            (now - new Date(y, m - 1, d)) / (1000 * 60 * 60 * 24),
        );
        return diffDays >= 7;
    };

    return (
        <div className="space-y-6" data-testid="makeups-page">
            <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">
                    Follow-ups
                </div>
                <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tight">
                    Make-up Summary
                </h1>
                <p className="text-slate-500 mt-2 max-w-xl">
                    Every excused session that hasn’t been rescheduled yet. Tap{" "}
                    <b>Resolve</b> once the make-up lesson is done.
                </p>
            </div>

            {pending.length === 0 ? (
                <EmptyState
                    icon={Sparkles}
                    title="All caught up"
                    message="No pending make-up sessions. Great work."
                    action={
                        <Link to="/students">
                            <Button
                                variant="outline"
                                className="rounded-xl h-11 px-5 font-bold border-mist"
                                data-testid="empty-makeup-students-btn"
                            >
                                View Students
                            </Button>
                        </Link>
                    }
                />
            ) : (
                <div
                    className="bg-white rounded-3xl border border-mist card-soft overflow-hidden"
                    data-testid="makeups-list"
                >
                    <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-mist/50 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 border-b border-mist">
                        <div className="col-span-4 sm:col-span-3">Student</div>
                        <div className="col-span-3 sm:col-span-2">Missed</div>
                        <div className="hidden sm:block sm:col-span-4">Remark</div>
                        <div className="col-span-5 sm:col-span-3 text-right">Action</div>
                    </div>
                    {pending.map((r) => (
                        <div
                            key={r.id}
                            className="grid grid-cols-12 gap-4 items-center px-6 py-4 border-b last:border-0 border-mist"
                            data-testid={`makeup-row-${r.id}`}
                        >
                            <div className="col-span-4 sm:col-span-3 min-w-0">
                                <Link
                                    to={`/students/${r.studentId}`}
                                    className="font-bold text-slate-900 hover:text-[#3D6385] truncate block"
                                    data-testid={`makeup-student-link-${r.id}`}
                                >
                                    {r.studentName}
                                </Link>
                            </div>
                            <div className="col-span-3 sm:col-span-2 min-w-0">
                                <div className="font-display font-bold text-slate-900">
                                    {formatDatePretty(r.date)}
                                </div>
                                {isOverdue(r.date) && (
                                    <span className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wide text-danger bg-danger-soft rounded-full px-2 py-0.5">
                                        Overdue
                                    </span>
                                )}
                            </div>
                            <div className="hidden sm:block sm:col-span-4 min-w-0">
                                <span className="text-sm text-slate-500 truncate block">
                                    {r.remark || <i className="text-slate-300">No remark</i>}
                                </span>
                            </div>
                            <div className="col-span-5 sm:col-span-3 flex justify-end">
                                <Button
                                    onClick={() => onResolve(r)}
                                    className="rounded-xl bg-success hover:bg-success text-white font-bold h-10 px-4"
                                    data-testid={`resolve-btn-${r.id}`}
                                >
                                    <CheckCircle2 size={15} strokeWidth={2.8} className="mr-2" />
                                    Resolve
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Resolved section */}
            <ResolvedSection />
        </div>
    );
}

function ResolvedSection() {
    const { attendance } = useData();
    const resolved = useMemo(
        () =>
            attendance
                .filter(
                    (r) =>
                        r.status === "Excused" && (r.remark || "").startsWith("Done"),
                )
                .sort((a, b) => b.date.localeCompare(a.date))
                .slice(0, 10),
        [attendance],
    );
    if (resolved.length === 0) return null;

    return (
        <div className="bg-white rounded-3xl border border-mist card-soft p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-4">
                <CalendarClock size={18} className="text-slate-400" strokeWidth={2.5} />
                <h2 className="font-display font-bold text-xl uppercase tracking-tight">
                    Recently resolved
                </h2>
            </div>
            <ul className="divide-y divide-mist">
                {resolved.map((r) => (
                    <li
                        key={r.id}
                        className="py-3 flex items-center justify-between gap-3"
                    >
                        <div>
                            <div className="font-bold text-slate-900">
                                {r.studentName}
                            </div>
                            <div className="text-xs text-slate-500">
                                {formatDatePretty(r.date)} · {r.remark}
                            </div>
                        </div>
                        <CheckCircle2
                            size={18}
                            className="text-success"
                            strokeWidth={2.5}
                        />
                    </li>
                ))}
            </ul>
        </div>
    );
}
